/*
  <wb-lazy-cards> fills a drawer carousel on demand instead of shipping its
  product cards in every page's HTML. The carousel shell sits in a <template>;
  the slides come from templates/collection.drawer-cards.liquid.

  Loads when the drawer opens, or earlier on intent (hover, focus or touch on a
  button that opens it). Outside a drawer it loads straight away. Responses
  are cached per URL, so a drawer that re-renders its markup (the cart after a
  change, search after clearing the query) refills from memory.

  data-container  selector for the hidden container, default the closest
                  .Drawer--wrapper
  data-intent     selector for its openers, default [data-toggle-id="<id>"]
  data-slide-tag  wrap each card in this tag instead of the swiper slide
*/
(function () {
  if (customElements.get('wb-lazy-cards')) return;

  var cache = new Map();

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fetchSlides(url) {
    if (!cache.has(url)) {
      var request = fetch(url, { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      });
      request.catch(function () {
        cache.delete(url);
      });
      cache.set(url, request);
    }
    return cache.get(url);
  }

  function isDrawerOpen(drawer) {
    return drawer.getAttribute('aria-hidden') === 'false' || drawer.getAttribute('aria-expanded') === 'true';
  }

  class WbLazyCards extends HTMLElement {
    connectedCallback() {
      if (this.loading) return;
      this.drawer = this.dataset.container ? document.querySelector(this.dataset.container) : this.closest('.Drawer--wrapper');

      if (!this.drawer || isDrawerOpen(this.drawer) || cache.has(this.dataset.url)) {
        this.load();
        return;
      }

      this.onIntent = this.onIntent.bind(this);
      this.observer = new MutationObserver(() => {
        if (isDrawerOpen(this.drawer)) this.load();
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['aria-hidden', 'aria-expanded'] });
      ['pointerover', 'focusin', 'touchstart'].forEach((type) => {
        document.addEventListener(type, this.onIntent, { passive: true });
      });
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      if (this.observer) this.observer.disconnect();
      if (this.onIntent) {
        ['pointerover', 'focusin', 'touchstart'].forEach((type) => {
          document.removeEventListener(type, this.onIntent);
        });
      }
    }

    onIntent(event) {
      var target = event.target;
      var intent = this.dataset.intent || (this.drawer.id && '[data-toggle-id="' + this.drawer.id + '"]');
      if (!(target instanceof Element) || !intent) return;
      if (target.closest(intent)) this.load();
    }

    load() {
      if (this.loading) return;
      this.loading = true;
      this.cleanup();

      var url = this.dataset.url;
      if (!url) return;

      fetchSlides(url)
        .then((html) => this.render(html))
        .catch((error) => {
          this.loading = false;
          if (window.slog) window.slog('custom', { message: 'wb-lazy-cards fetch failed', extra: { url: url, error: String(error) } });
        });
    }

    render(html) {
      var shell = this.querySelector('template');
      if (!shell) return;

      html = html
        .split('__WB_LIST_ID__').join(escapeHtml(this.dataset.listId || ''))
        .split('__WB_LIST_NAME__').join(escapeHtml(this.dataset.listName || ''));

      var parsed = document.createElement('div');
      parsed.innerHTML = html;

      var limit = parseInt(this.dataset.limit, 10) || 12;
      var slides = Array.prototype.slice.call(parsed.children, 0, limit);
      // Same order as the old Liquid loop: limit first, then drop sold out.
      if (this.hasAttribute('data-available-only')) {
        slides = slides.filter(function (slide) {
          return slide.getAttribute('data-available') === 'true';
        });
      }
      if (!slides.length) return;

      var fragment = shell.content.cloneNode(true);
      var wrapper = fragment.querySelector('[data-lazy-cards-target]') || fragment.querySelector('.swiper-wrapper');
      var slideTag = this.dataset.slideTag;
      slides.forEach(function (slide) {
        if (slideTag) {
          var item = document.createElement(slideTag);
          item.append.apply(item, slide.childNodes);
          slide = item;
        }
        wrapper.appendChild(slide);
      });
      this.replaceChildren(fragment);
      this.dispatchEvent(new CustomEvent('wb-lazy-cards:loaded', { bubbles: true }));
    }
  }

  customElements.define('wb-lazy-cards', WbLazyCards);
})();
